import { useSyncExternalStore } from 'react';

import { type ChatComposerReference } from '@apps/web/lib/chat_composer_draft';

// Matches the API's max_length on activity_event_ids.
export const ACTIVITY_HANDOFF_MAX = 20;

export type ActivityHandoffItem = {
    readonly activityEventId: string;
    readonly title: string;
    readonly subtitle: string | null;
    readonly personLabel: string | null;
    readonly references: ReadonlyArray<ChatComposerReference>;
};

type ActivityHandoffState = {
    readonly contextKey: string | null;
    readonly selectedEventIds: ReadonlySet<string>;
    readonly orderedItems: ReadonlyArray<ActivityHandoffItem>;
};

const EMPTY_STATE: ActivityHandoffState = {
    contextKey: null,
    selectedEventIds: new Set(),
    orderedItems: [],
};

let state = EMPTY_STATE;
const listeners = new Set<() => void>();

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

export function buildActivityHandoffContextKey(
    organizationId: string | null,
    mode: string,
): string {
    return `${organizationId ?? 'none'}:${mode}`;
}

function hasSameOrderedIds(
    left: ReadonlyArray<ActivityHandoffItem>,
    right: ReadonlyArray<ActivityHandoffItem>,
): boolean {
    return (
        left.length === right.length &&
        left.every(
            (item, index) =>
                item.activityEventId === right[index]?.activityEventId,
        )
    );
}

function dedupeByEventId(
    items: ReadonlyArray<ActivityHandoffItem>,
): Array<ActivityHandoffItem> {
    const seen = new Set<string>();
    const deduped: Array<ActivityHandoffItem> = [];
    for (const item of items) {
        if (seen.has(item.activityEventId)) {
            continue;
        }
        seen.add(item.activityEventId);
        deduped.push(item);
    }
    return deduped;
}

export const activityHandoffStore = {
    getState(): ActivityHandoffState {
        return state;
    },
    // Replaces the whole selection. Snapshots survive board refetches and
    // unmount so the chat tray can always render them.
    setSelection(
        contextKey: string,
        items: ReadonlyArray<ActivityHandoffItem>,
    ) {
        const orderedItems = dedupeByEventId(items).slice(
            0,
            ACTIVITY_HANDOFF_MAX,
        );
        if (
            state.contextKey === contextKey &&
            hasSameOrderedIds(state.orderedItems, orderedItems)
        ) {
            return;
        }

        state = {
            contextKey,
            selectedEventIds: new Set(
                orderedItems.map((item) => item.activityEventId),
            ),
            orderedItems,
        };
        emitChange();
    },
    removeItem(activityEventId: string) {
        if (!state.selectedEventIds.has(activityEventId)) {
            return;
        }

        const orderedItems = state.orderedItems.filter(
            (item) => item.activityEventId !== activityEventId,
        );
        state =
            orderedItems.length === 0
                ? EMPTY_STATE
                : {
                      contextKey: state.contextKey,
                      selectedEventIds: new Set(
                          orderedItems.map((item) => item.activityEventId),
                      ),
                      orderedItems,
                  };
        emitChange();
    },
    // Removes several ids in one rebuild so an in-flight send can drop the
    // cards it referenced without wiping cards selected during the send.
    removeItems(activityEventIds: ReadonlyArray<string>) {
        const removeIds = new Set(
            activityEventIds.filter((eventId) =>
                state.selectedEventIds.has(eventId),
            ),
        );
        if (removeIds.size === 0) {
            return;
        }

        const orderedItems = state.orderedItems.filter(
            (item) => !removeIds.has(item.activityEventId),
        );
        state =
            orderedItems.length === 0
                ? EMPTY_STATE
                : {
                      contextKey: state.contextKey,
                      selectedEventIds: new Set(
                          orderedItems.map((item) => item.activityEventId),
                      ),
                      orderedItems,
                  };
        emitChange();
    },
    clear() {
        if (state === EMPTY_STATE) {
            return;
        }

        state = EMPTY_STATE;
        emitChange();
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function useActivityHandoff(): ActivityHandoffState {
    return useSyncExternalStore(
        activityHandoffStore.subscribe,
        activityHandoffStore.getState,
    );
}

export type { ActivityHandoffState };
